import { ApiProject, ApiClient } from "@incendium/api";
import { Autocomplete, OutlinedTextFieldProps, TextField } from "@mui/material";
import { initializeClientsThunk } from "Actions";
import useAllProjects from "features/project/hooks/useAllProjects";
import { sortNum } from "Helpers/arrays";
import { CallbackOrVal } from "Interfaces";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMount } from "react-use";
import { clientListSelector } from "Selectors/clientListSelector";

interface IAllProjectsSelectorProps
  extends Omit<OutlinedTextFieldProps, "onChange"> {
  project: ApiProject | null;
  setProject: (v: CallbackOrVal<ApiProject | null>) => void;
  filterFn?: (v: ApiProject) => boolean;
}

interface IAllProjectsSelectorInnerProps extends IAllProjectsSelectorProps {
  clients: ApiClient[];
  allProjects: ApiProject[];
}

function AllProjectsSelectorInner({
  project,
  setProject,
  filterFn,
  clients,
  allProjects,
  ...rest
}: IAllProjectsSelectorInnerProps) {
  const [projects, setProjects] = useState<ApiProject[]>([]);



  useEffect(() => {
    if (!clients.length || !allProjects.length) {
      return;
    }

    // Process projects directly in the effect to avoid dependency issues
    setProjects(
      sortNum(
        (allProjects || []).map((p) => ({
          ...p,
          client: {
            id: p.clientId,
            businessName: clients.find((c) => c.id === p.clientId)?.businessName || "",
          },
        })),
        "clientId"
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, allProjects]);

  const selectableProjects = useMemo(() => {
    if (!filterFn) {
      return projects;
    }
    return projects.filter(filterFn);
  }, [filterFn, projects]);


  return (
    <Autocomplete
      id="project-picker"
      size="small"
      onChange={(e, value) => {
        setProject(value);
      }}
      disableClearable
      value={project || {}}
      isOptionEqualToValue={(option, value) => {
        if (!value || !value.id) return false;
        return option?.id === value?.id;
      }}
      options={selectableProjects}
      getOptionLabel={(o) => o.name || ""}
      groupBy={(o) => o.client?.businessName || ""}
      renderInput={(params) => (
        <TextField {...params} label="Select a project" {...rest} />
      )}
    />
  );
}

function AllProjectsSelector({
  project,
  setProject,
  filterFn,
  ...rest
}: IAllProjectsSelectorProps) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const clients = useSelector(clientListSelector);
  const { allProjects, fetched } = useAllProjects();
  useMount(async () => {
    dispatch(initializeClientsThunk());
  });


  useEffect(() => {
    if (clients.hasLoaded) {
      setLoading(false);
    }
  }, [clients.hasLoaded]);
  if (!clients || loading || !fetched) {
    return <></>;
  }

  return <AllProjectsSelectorInner project={project} setProject={setProject} filterFn={filterFn} clients={clients.list} allProjects={allProjects} {...rest} />;
}

export default AllProjectsSelector;
